import React, { useContext } from 'react';
import { Builder } from '@builder.io/react';
import { fallbackImage } from '@/api/product/mapping';
import { ArticleHeader as DSArticleHeader } from '@/components/shared';
import { useArticle } from '@/contexts/ArticleProvider';
import { useDamFile } from '@/contexts/DamFileProvider';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { useGetSettings } from '@/generated/settings';
import { useGetTranslations } from '@/generated/translations';
import { formatDate } from '@/helpers/dateHelpers';

/**
 * CMS ArticleHeader component, showing the top of the page it is placed on.
 *
 * It takes no inputs, so an editor only places the block, and the headline, image, labels
 * and dates stay maintained once on the page itself. Any page carrying those fields can
 * use it, not only the articles an overview lists.
 */
export const ArticleHeader: React.FunctionComponent = () => {
	const article = useArticle();
	const { segmentationId } = useContext(SegmentationContext);
	const { data: settings } = useGetSettings(segmentationId);
	const { data: translations } = useGetTranslations(segmentationId);
	const damImage = useDamFile(article?.featuredImage ?? null);

	const cultureCode = settings?.data.cultureCode ?? '';
	// The CMS keeps the references in the order the editor happened to add them, which says
	// nothing to a reader. Sorted against the site's own culture, so the server and the
	// browser agree on the order and æ, ø and å land where the language puts them.
	const categories = React.useMemo(
		() => (article?.categories ? [...article.categories].sort((a, b) => a.localeCompare(b, cultureCode)) : undefined),
		[article?.categories, cultureCode],
	);

	if (!article?.headline) {
		// Give the editor something to select, instead of an invisible block
		return Builder.isEditing ? <p>This page has no headline to show yet.</p> : null;
	}

	return (
		<DSArticleHeader
			categories={categories}
			headline={article.headline}
			image={damImage ?? fallbackImage}
			publicationDate={
				article.publicationDate
					? formatDate(new Date(article.publicationDate), settings?.data.cultureCode ?? '')
					: undefined
			}
			readingTime={article.readingTime}
			labels={article.visibleCardLabels}
			relatedTagsLabel={translations?.data.knowledgeHub.relatedTags}
		/>
	);
};
